/**
 *  @class
 *  @function PredictiveSearch
 *  Enhanced version with Style4 support
 */
class PredictiveSearch {
  constructor() {
    this.isStyle4Original = document.querySelector('.search-form-container') !== null
    this._bindMethods()
    this._initMode()
    this.setupEventListeners()
    window.addEventListener('resize', this._onResize)
  }

  _bindMethods() {
    this._onResize = this._onResize.bind(this)
    this.onToggleClick = this.onToggleClick.bind(this)
    this.onFormSubmit = this.onFormSubmit.bind(this)
    this.onChange = this.onChange.bind(this)
    this._onDocumentDown = this._onDocumentDown.bind(this)
    this._onKeyUp = this._onKeyUp.bind(this)
  }

  _initMode() {
    const isSmall = window.innerWidth <= 1200
    this.useDefault = !this.isStyle4Original || (this.isStyle4Original && isSmall)
    this.isStyle4 = this.isStyle4Original && !isSmall
    if (this.useDefault) this.initDefault()
    else this.initStyle4()
  }

  initStyle4() {
    this.container = document.querySelector('.search-form-container')
    this.input = this.container.querySelector('.search-field')
    this.close_button = this.container.querySelector('.thb-search-close')
    this.predictiveSearchResults = document.querySelector('.thb-quick-search--results')
    this.loadingOverlay = this.container.querySelector('.loading-overlay')
    this.resultsWrapper = document.querySelector('.thb-quick-search-container-results-area')
  }

  initDefault() {
    this.container = document.querySelector('.thb-quick-search')
    this.button = document.getElementById('quick-search')
    this.input = this.container.querySelector('input[type="search"]')
    this.close_button = this.container.querySelector('.thb-search-close')
    this.predictiveSearchResults = this.container.querySelector('.thb-quick-search--results')
    this.loadingOverlay = this.container.querySelector('.loading-overlay')
  }

  removeEventListeners() {
    const form = this.container.querySelector('form.searchform')
    if (form) form.removeEventListener('submit', this.onFormSubmit)
    if (this.input) {
      this.input.removeEventListener('input', this._debouncedChange)
      this.input.removeEventListener('focus', this._onFocus)
    }
    if (!this.isStyle4 && this.button) {
      this.button.removeEventListener('click', this.onToggleClick)
    }
    if (this.close_button) {
      this.close_button.removeEventListener('click', this.close)
    }
    if (this.isStyle4) {
      document.removeEventListener('mousedown', this._onDocumentDown)
    }
    document.removeEventListener('keyup', this._onKeyUp)
  }

  setupEventListeners() {
    this._debouncedChange = debounce(this.onChange, 300)
    const form = this.container.querySelector('form.searchform')
    if (form) form.addEventListener('submit', this.onFormSubmit)
    this.input.addEventListener('input', this._debouncedChange)

    if (this.isStyle4) {
      this._onFocus = () => this.openResults()
      this.input.addEventListener('focus', this._onFocus)
      document.addEventListener('mousedown', this._onDocumentDown)
    } else {
      if (this.button) this.button.addEventListener('click', this.onToggleClick)
    }

    this.close_button.addEventListener('click', this.close)
    document.addEventListener('keyup', this._onKeyUp)
  }

  _onDocumentDown(e) {
    if (this.resultsWrapper &&
        !this.container.contains(e.target) &&
        !this.resultsWrapper.contains(e.target)
    ) this.closeResults()
  }

  _onKeyUp(e) {
    if (e.key === 'Escape') this.close()
  }

  _onResize() {
    const prevUseDefault = this.useDefault
    this._initMode()
    if (prevUseDefault !== this.useDefault) {
      this.close()
      if (this.resultsWrapper) this.closeResults()
      this.removeEventListeners()
      this.setupEventListeners()
    }
  }

  onToggleClick(event) {
    event.preventDefault()
    const header = document.querySelector('.header-section')
    header.classList.toggle('search-open')
    if (header.classList.contains('search-open')) {
      setTimeout(() => this.input.focus({ preventScroll: true }), 100)
      dispatchCustomEvent('search:open')
    }
  }

  getQuery() {
    return this.input.value.trim()
  }

  onChange() {
    const term = this.getQuery()
    if (!term.length) {
      this.predictiveSearchResults.classList.remove('active')
      return
    }
    this.getSearchResults(term)
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) event.preventDefault()
  }

  getSearchResults(searchTerm) {
    this.predictiveSearchResults.classList.add('loading')
    fetch(
      `${theme.routes.predictive_search_url}` +
      `?q=${encodeURIComponent(searchTerm)}` +
      `&resources[type]=product,article,query` +
      `&resources[limit]=10` +
      `&section_id=predictive-search`
    )
      .then(response => {
        this.predictiveSearchResults.classList.remove('loading')
        if (!response.ok) throw new Error(response.status)
        return response.text()
      })
      .then(text => {
        const parser = new DOMParser()
        const html   = parser.parseFromString(text, 'text/html')
        const resultsMarkup = html.querySelector('#shopify-section-predictive-search').innerHTML
        this.renderSearchResults(resultsMarkup)
      })
      .catch(error => console.error('PredictiveSearch error:', error))
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup
    this.predictiveSearchResults.classList.add('active')
    this.categoryToggle()
    if (this.isStyle4) this.openResults()
  }

  openResults() {
    const el = this.resultsWrapper
    el.style.maxHeight = el.scrollHeight + 'px'
    el.classList.add('open')
  }

  closeResults() {
    const el = this.resultsWrapper
    el.style.maxHeight = '0px'
    el.classList.remove('open')
  }

  open() {
    document.querySelector('.header-section').classList.add('search-open')
  }

  close() {
    document.querySelector('.header-section').classList.remove('search-open')
    if (this.isStyle4) this.closeResults()
  }

  categoryToggle() {
    this.category_toggle = this.container.querySelectorAll('.search-categories a')
    this.category_toggle.forEach(link => {
      link.addEventListener('click', event => {
        this.category_toggle.forEach(el => el.classList.remove('active'))
        link.classList.add('active')
        const target = link.getAttribute('href')
        this.container.querySelectorAll('.search-results').forEach(section => section.classList.remove('active'))
        this.container.querySelector(target).classList.add('active')
        event.preventDefault()
      })
    })
  }
}

window.addEventListener('load', () => {
  if (typeof PredictiveSearch !== 'undefined') new PredictiveSearch()
})